import { defineStore } from 'pinia'
import { ref } from 'vue'
import '@/styles/theme-transition.css'

// 导入主题文件
import '@/styles/generated/themes/light.gen.css'
import '@/styles/generated/themes/dark.gen.css'

// 类型定义
export type ThemeMode = 'light' | 'dark'

// 配置常量
const CONFIG = {
  STORAGE_KEY: 'app-theme',
  SYSTEM_THEME_QUERY: '(prefers-color-scheme: dark)',
  DEBOUNCE_DELAY: 50,
  TRANSITION_DURATION: 300,
} as const

// 防抖定时器
let debounceTimer: number | null = null

/**
 * 切换主题
 * 更新 data-theme 属性并添加过渡动画
 */
function switchTheme(theme: ThemeMode): void {
  if (debounceTimer !== null) {
    window.clearTimeout(debounceTimer)
  }

  debounceTimer = window.setTimeout(() => {
    document.documentElement.classList.add('theme-transitioning')
    document.documentElement.setAttribute('data-theme', theme)

    requestAnimationFrame(() => {
      setTimeout(() => {
        document.documentElement.classList.remove('theme-transitioning')
      }, CONFIG.TRANSITION_DURATION)
    })

    debounceTimer = null
  }, CONFIG.DEBOUNCE_DELAY)
}

export const useThemeStore = defineStore('theme', () => {
  // 从 localStorage 获取保存的主题，如果没有则使用默认值
  const theme = ref<ThemeMode>(
    (localStorage.getItem(CONFIG.STORAGE_KEY) as ThemeMode) || 'light'
  )

  /**
   * 获取系统主题偏好
   */
  function getSystemThemePreference(): ThemeMode {
    return window.matchMedia(CONFIG.SYSTEM_THEME_QUERY).matches
      ? 'dark'
      : 'light'
  }

  /**
   * 保存主题设置
   */
  function saveTheme(mode: ThemeMode): void {
    localStorage.setItem(CONFIG.STORAGE_KEY, mode)
  }

  /**
   * 切换主题
   */
  function toggleTheme(): void {
    setTheme(theme.value === 'light' ? 'dark' : 'light')
  }

  /**
   * 设置主题
   */
  function setTheme(mode: ThemeMode): void {
    theme.value = mode
    saveTheme(mode)
    switchTheme(mode)
  }

  /**
   * 初始化主题
   */
  function initTheme(): void {
    // 如果本地存储中没有主题设置，则检查系统主题偏好
    if (!localStorage.getItem(CONFIG.STORAGE_KEY)) {
      theme.value = getSystemThemePreference()
      saveTheme(theme.value)
    }

    // 应用当前主题
    switchTheme(theme.value)

    // 监听系统主题变化
    window
      .matchMedia(CONFIG.SYSTEM_THEME_QUERY)
      .addEventListener('change', e => {
        if (!localStorage.getItem(CONFIG.STORAGE_KEY)) {
          setTheme(e.matches ? 'dark' : 'light')
        }
      })
  }

  return {
    theme,
    toggleTheme,
    setTheme,
    initTheme,
  }
})
